import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { CallMediaPermissionValue, IDeviceInfo } from '@gem/call/CallStore/Call.types';
import { MeStore } from '@gem/call/CallStore/Me/Me.store';
import { useCallContext } from '@gem/call/CallView/CallView.context';
import {
  ICallPersonalizedPermissions,
  PermissionsPersonalizedValue,
  PermissionsTypes,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { useTranslation } from '@gem/modules/i18n';
import { popupsTasks } from '@gem/popups';

interface ICallAudioButtonPresenter {
  ongoingCall: Call;
  audioPermissions: PermissionsPersonalizedValue | null;
  meStore: MeStore;
  audioPermission: CallMediaPermissionValue;
  isAudioMuted: boolean;
  isCallStarted: boolean;
  isAudioPopoverOpened: boolean;
  micControlEnabled: boolean;
  isMeInitiatorOwnerOrAdmin: boolean;
  permissions: ICallPersonalizedPermissions | null;
  audioInputDevices: IDeviceInfo[];
  audioOutputDevices: IDeviceInfo[];
  currentAudioInputDeviceId: string | null;
  currentAudioOutputDeviceId: string | null;
  setCurrentDeviceId(kind: MediaDeviceKind, deviceId: string): void;
  unmuteAudio(): Promise<void>;
  closeAudioPopover(): void;
  onAudioArrowClick(): void;
  muteAudio(): void;
  onAudioPopoverOpen?(): void;
  setMicControlEnabled(value: boolean): void;
}

interface ICallAudioButtonPresenterProps {
  checkAndSendPermissionRequest(permissionType: PermissionsTypes): Promise<boolean>;
  onAudioPopoverOpen?(): void;
  permissions: ICallPersonalizedPermissions | null;
  audioPermission: CallMediaPermissionValue;
  micControlEnabled: boolean;
}

export const useCallAudioButtonPresenter = (
  props: ICallAudioButtonPresenterProps
): ICallAudioButtonPresenter => {
  const context = useCallContext();
  const { t } = useTranslation();

  const presenter = useLocalObservable<ICallAudioButtonPresenter>(() => ({
    permissions: props.permissions,
    isAudioPopoverOpened: false,
    micControlEnabled: props.micControlEnabled,
    audioPermission: CallMediaPermissionValue.Initial,
    onAudioPopoverOpen: undefined,
    get ongoingCall(): Call {
      return context.call;
    },

    get audioInputDevices(): IDeviceInfo[] {
      return presenter.ongoingCall.mediaDevices.devices.audioinput;
    },

    get audioOutputDevices(): IDeviceInfo[] {
      return presenter.ongoingCall.mediaDevices.devices.audiooutput;
    },

    get currentAudioInputDeviceId(): string | null {
      return presenter.ongoingCall.mediaDevices.currentDeviceIds.audioinput;
    },

    get currentAudioOutputDeviceId(): string | null {
      return presenter.ongoingCall.mediaDevices.currentDeviceIds.audiooutput;
    },

    get audioPermissions(): PermissionsPersonalizedValue | null {
      return presenter.permissions && presenter.permissions[PermissionsTypes.SwitchOnMic]
        ? presenter.permissions[PermissionsTypes.SwitchOnMic]
        : null;
    },

    get meStore(): MeStore {
      return presenter.ongoingCall.meStore;
    },

    get isAudioMuted(): boolean {
      return presenter.meStore.isAudioMuted;
    },

    get isCallStarted(): boolean {
      return presenter.ongoingCall.isStarted;
    },

    get isMeInitiatorOwnerOrAdmin(): boolean {
      return presenter.ongoingCall.isMeInitiatorOwnerOrAdmin;
    },

    setCurrentDeviceId(kind: MediaDeviceKind, deviceId: string): void {
      context.callController.setCurrentDeviceId(kind, deviceId);
    },

    async unmuteAudio(): Promise<void> {
      const canUnmuteMicrophone = await props.checkAndSendPermissionRequest(
        PermissionsTypes.SwitchOnMic
      );

      if (!canUnmuteMicrophone) return;

      if (presenter.ongoingCall.meStore.isVideoMuted && !presenter.ongoingCall.canUnmute) {
        popupsTasks.showAlert(t('premium_extended_calls_broadcasters_limit_exceeded'));
        return;
      }

      await context.callController.unmuteAudio(true);
    },

    muteAudio(): void {
      context.callController.muteAudio();
    },

    closeAudioPopover(): void {
      presenter.isAudioPopoverOpened = false;
    },

    async onAudioArrowClick(): Promise<void> {
      if (presenter.audioPermission === CallMediaPermissionValue.Initial) {
        const hasAudioPermission = await context.callController.checkAudioPermission();

        if (hasAudioPermission) {
          presenter.onAudioPopoverOpen && presenter.onAudioPopoverOpen();
          presenter.isAudioPopoverOpened = true;
        }
      } else {
        presenter.onAudioPopoverOpen && presenter.onAudioPopoverOpen();
        presenter.isAudioPopoverOpened = true;
      }
    },

    setMicControlEnabled(value: boolean): void {
      presenter.micControlEnabled = value;
    },
  }));

  useEffect(() => {
    presenter.onAudioPopoverOpen = props.onAudioPopoverOpen;
  }, [props.onAudioPopoverOpen]);

  useEffect(() => {
    presenter.permissions = props.permissions;
    presenter.audioPermission = props.audioPermission;
  }, [props.permissions, props.audioPermission]);

  useEffect(() => {
    if (
      !presenter.isAudioMuted &&
      presenter.audioPermissions !== PermissionsPersonalizedValue.Always &&
      !presenter.isMeInitiatorOwnerOrAdmin
    ) {
      presenter.muteAudio();
    }
  }, [presenter.isAudioMuted, presenter.audioPermissions, presenter.isMeInitiatorOwnerOrAdmin]);

  useEffect(() => {
    presenter.setMicControlEnabled(props.micControlEnabled);
  }, [props.micControlEnabled]);

  return presenter;
};
